/**
 * Hook to subscribe to ChartStore events with debouncing.
 * Useful for components that don't need immediate updates during rapid interactions.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getChartLogger } from "~utils/ticker-chart";
import type { ChartStore, ChartStoreEvent } from "~types/types";

/**
 * Subscribe to one or more ChartStore events with debounced updates.
 * Component only re-renders after the specified delay has passed since the last event.
 * Useful for expensive components (like indicators) that don't need real-time updates.
 *
 * @param store - ChartStore instance
 * @param events - Single event or array of events to subscribe to
 * @param delay - Debounce delay in milliseconds (default: 100)
 * @returns Current revision number (increments after debounce period)
 *
 * @example
 * ```tsx
 * // Subscribe to viewport changes with 100ms debounce
 * const Indicator = ({ store }) => {
 *   useDebouncedSubscription(store, ['viewport', 'scales'], 100);
 *   // Component re-renders only after zoom/pan interaction ends
 * };
 * ```
 */
export function useDebouncedSubscription(
  store: ChartStore,
  events: ChartStoreEvent | ChartStoreEvent[],
  delay = 100
): number {
  const [revision, setRevision] = useState(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Ref for logging which events triggered the update
  const eventsRef = useRef(events);

  eventsRef.current = events;

  const debouncedUpdate = useCallback(() => {
    // Cancel any pending update
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
    }

    // Schedule new update
    timeoutRef.current = setTimeout(() => {
      const logger = getChartLogger();
      const eventStr = Array.isArray(eventsRef.current)
        ? eventsRef.current.join(",")
        : eventsRef.current;

      logger.debug(`debouncedUpdate for events: ${eventStr}`);
      setRevision((prev) => prev + 1);
      timeoutRef.current = null;
    }, delay);
  }, [delay]);

  // Stabilize the events array to prevent infinite re-subscriptions
  const eventArray = useMemo(
    () => (Array.isArray(events) ? events : [events]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [Array.isArray(events) ? events.join(",") : events]
  );

  // Subscribe to the current store instance
  useEffect(() => {
    const unsubscribe = store.subscribeMany(eventArray, debouncedUpdate);

    return () => {
      unsubscribe();
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [store, eventArray, debouncedUpdate]);

  return revision;
}
